import { HubFeature } from '@robovision/hub'
import {
  DataTag,
  queryOptions,
} from '@tanstack/vue-query'
import {
  type AnyEntitlement,
  Entitlement,
  EntitlementKind,
  type FlagEntitlement,
  type LimitEntitlement,
} from '~domain/Entitlement'
import { hubApi } from '~services/api'
import { queryClient } from '~services/query-client'
import { RETHROW_ERROR_AFTER_HANDLING } from '~utils/api/error-handling'


export const ENTITLEMENTS_QUERY_KEY = ['hubApi', 'entitlements'] as DataTag<string[], AnyEntitlement[]>

export async function refetchEntitlements (): Promise<void> {
  await queryClient.fetchQuery({
    ...getEntitlementsQueryOptions(),
    // The entitlements query is set to load from cache. This forces it
    // to load fresh data from the server.
    staleTime: 0,
  })
}

export function getLimitEntitlement (
  entitlements: readonly AnyEntitlement[],
  feature: HubFeature,
): LimitEntitlement | undefined {
  const entitlement = entitlements.find((e) => e.feature === feature)

  return entitlement?.kind === EntitlementKind.LIMIT ? entitlement : undefined
}

export function getFlagEntitlement (
  entitlements: readonly AnyEntitlement[],
  feature: HubFeature,
): FlagEntitlement | undefined {
  const entitlement = entitlements.find((e) => e.feature === feature)

  return entitlement?.kind === EntitlementKind.FLAG ? entitlement : undefined
}

export function isFlagEntitlementGranted (
  entitlements: readonly AnyEntitlement[],
  feature: HubFeature,
): boolean {
  return !!getFlagEntitlement(entitlements, feature)?.isGranted
}

async function fetchEntitlements (): Promise<AnyEntitlement[]> {
  const { data } = await hubApi.entitlements.getEntitlements(undefined, RETHROW_ERROR_AFTER_HANDLING)

  return data.map(Entitlement.fromResponse)
}

export function getEntitlementsQueryOptions () {
  return queryOptions({
    gcTime: Infinity,
    staleTime: 'static',
    queryFn: fetchEntitlements,
    /**
     * In order for queryOptions to properly infer types
     * queryKey should NOT be a data tag but a simple array
     */
    queryKey: ENTITLEMENTS_QUERY_KEY as string[],
  })
}
